// src/components/Customer/CustomerContacts.js
import React, { useEffect, useState } from "react";
import {
    Grid,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Button,
    TextField,
    InputAdornment,
    IconButton,
    Pagination,
    Stack,
    Menu,
    MenuItem,
    ListItemIcon,
    ListItemText,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

import useStyles from "../../assets/styles/SupMangComp.style";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import noTableData from "../../assets/images/noTableData.svg";
import { Notification } from "../../utils/Notification";
import API from "../../utils/Axios.utils";
import useDebounce from "../../utils/useDebounce";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import EditIcon from "@mui/icons-material/Edit";
import CustomModel from "../../utils/CustomModel";

const CustomerContacts = () => {
    const classes = useStyles();
    const navigate = useNavigate();
    const location = useLocation();

    const [alert, setAlert] = useState(false);
    const [alertContent, setAlertContent] = useState("");
    const [alertType, setAlertType] = useState("");

    const [searchText, setSearchText] = useState("");
    const debouncedSearch = useDebounce(searchText, 500);

    const [page, setPage] = useState(1);
    const [contacts, setContacts] = useState([]);
    const [listCount, setListCount] = useState(0);

    const [showLoader, setShowLoader] = useState(false);

    // we still take customer name from navigate state for title
    const customer = location.state?.customerDetails || null;
    const customerId = customer?.customer_id || 0;
    const handleAlert = () => setAlert(false);
    const handlePageChange = (e, value) => setPage(value);

    const token = JSON.parse(localStorage.getItem("userLoggedIn")).token;
    const config = { headers: { Authorization: `Bearer ${token}` } };

    const getContacts = async () => {
        try {
            setShowLoader(true);

            let url = `/api/customer/allCustomersContacts/${customerId}?page=${page}&pageSize=10`;
            if (debouncedSearch) {
                url += `&search=${encodeURIComponent(debouncedSearch)}`;
            }

            const res = await API.get(url, config);

            setShowLoader(false);
            setContacts(res?.data?.contacts || []);
            setListCount(res?.data?.totalCount || 0);
        } catch (error) {
            setShowLoader(false);
            setAlertContent(
                error?.response?.data?.message || "Failed to fetch contact persons"
            );
            setAlertType("error");
            setAlert(true);
        }
    };

    useEffect(() => {
        getContacts();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, debouncedSearch]);

    const pageCount = Math.max(1, Math.ceil(listCount / 10));

    const [editOpen, setEditOpen] = useState(false);
    const [editingContact, setEditingContact] = useState(null);
    const [editForm, setEditForm] = useState({ name: "", email: "", phone: "" });

    const openEditDialog = (contact) => {
        setEditingContact(contact);
        setEditForm({
            name: contact.name || "",
            email: contact.email || "",
            phone: contact.phone || "",
        });
        setEditOpen(true);
    };

    const closeEditDialog = () => {
        setEditOpen(false);
        setEditingContact(null);
    };

    const handleToggleStatus = async (contact) => {
        const isActive = contact.is_deleted === "N" && contact.is_active === "Y";

        try {
            const token = JSON.parse(localStorage.getItem("userLoggedIn")).token;
            const config = { headers: { Authorization: `Bearer ${token}` } };

            await API.patch(
                `/api/customer/${customerId}/contacts/${contact.contact_id}/status`,
                {
                    status: isActive ? "inactive" : "active", // 👈 map to active/inactive
                },
                config
            );

            setAlertContent(
                isActive ? "Contact marked Inactive" : "Contact marked Active"
            );
            setAlertType("success");
            setAlert(true);
            getContacts();
        } catch (err) {
            setAlertContent(
                err?.response?.data?.message || "Failed to update status"
            );
            setAlertType("error");
            setAlert(true);
        }
    };

    const handleEditSave = async () => {
        if (!editingContact) return;
        try {
            const token = JSON.parse(localStorage.getItem("userLoggedIn")).token;
            const config = { headers: { Authorization: `Bearer ${token}` } };

            await API.put(
                `/api/customer/${customerId}/contacts/${editingContact.contact_id}`,
                {
                    name: editForm.name,
                    email: editForm.email,
                    phone: editForm.phone,
                },
                config
            );

            setAlertContent("Contact updated successfully");
            setAlertType("success");
            setAlert(true);
            setEditOpen(false);
            setEditingContact(null);
            getContacts();
        } catch (err) {
            setAlertContent(
                err?.response?.data?.message || "Failed to update contact"
            );
            setAlertType("error");
            setAlert(true);
        }
    };

    const ContactMoreMenu = ({ contact, onEditClick, onToggleStatus }) => {
        const [anchorEl, setAnchorEl] = React.useState(null);
        const open = Boolean(anchorEl);

        const handleOpen = (e) => setAnchorEl(e.currentTarget);
        const handleClose = () => setAnchorEl(null);

        const handleEdit = () => {
            handleClose();
            onEditClick(contact);
        };

        const handleToggle = () => {
            handleClose();
            onToggleStatus(contact);
        };

        const isActive = contact.is_deleted === "N" && contact.is_active === "Y";

        return (
            <>
                <IconButton size="small" onClick={handleOpen}>
                    <MoreVertIcon fontSize="small" />
                </IconButton>

                <Menu
                    anchorEl={anchorEl}
                    open={open}
                    onClose={handleClose}
                    anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                    transformOrigin={{ vertical: "top", horizontal: "right" }}
                >
                    <MenuItem onClick={handleEdit}>
                        <ListItemIcon>
                            <EditIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary="Edit" />
                    </MenuItem>

                    <MenuItem onClick={handleToggle}>
                        <ListItemIcon>
                            <span
                                style={{
                                    width: 10,
                                    height: 10,
                                    borderRadius: "50%",
                                    backgroundColor: isActive ? "red" : "green",
                                }}
                            />
                        </ListItemIcon>
                        <ListItemText primary={isActive ? "Inactive" : "Active"} />
                    </MenuItem>
                </Menu>
            </>
        );
    };

    return (
        <>
            <Grid container className={classes.mainCon}>
                <Notification
                    open={alert}
                    handleClose={handleAlert}
                    type={alertType}
                    content={alertContent}
                />

                {/* Top bar: title left, search + back button right */}
                <Grid item xs={12} style={{ marginTop: "20px" }}>
                    <Grid
                        container
                        xs={12}
                        style={{ display: "flex", justifyContent: "flex-start" }}
                    >
                        <Button
                            // variant="contained"
                            sx={{ mb: 2 }}
                            startIcon={<ArrowBackIcon />}
                            onClick={() => navigate("/customers")}
                        // style={{ background: "#2777AA", width: "214px" }}
                        >
                            Back to Customers
                        </Button>
                    </Grid>

                    <Grid
                        container
                        xs={12}
                        style={{
                            marginTop: "2%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                        }}
                    >
                        <Grid item xs={6} style={{ display: "flex", alignItems: "center" }}>
                            <span className={classes.headingText}>
                                {customer ? `${customer.name} - ` : ""} Contact Persons
                            </span>
                        </Grid>

                        <Grid
                            item
                            xs={6}
                            style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "flex-end",
                            }}
                        >
                            <TextField
                                name="search"
                                label="Search by Contact Name"
                                variant="outlined"
                                size="small"
                                style={{ backgroundColor: "#fff", width: "230px" }}
                                value={searchText}
                                onChange={(e) => setSearchText(e.target.value)}
                                InputProps={{
                                    endAdornment: searchText ? (
                                        <InputAdornment position="end">
                                            <IconButton onClick={() => setSearchText("")}>
                                                <CloseIcon />
                                            </IconButton>
                                        </InputAdornment>
                                    ) : null,
                                }}
                            />
                        </Grid>
                    </Grid>
                </Grid>

                {/* Table layout same as Customers */}
                <Grid container xs={12} className={classes.tableGrid}>
                    {showLoader ? (
                        <div
                            style={{
                                height: "300px",
                                width: "100%",
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                flexDirection: "column",
                            }}
                        >
                            <span>Loading...</span>
                        </div>
                    ) : contacts.length === 0 ? (
                        <div
                            style={{
                                height: "300px",
                                width: "100%",
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                flexDirection: "column",
                            }}
                        >
                            <img src={noTableData} width="60px" alt="No Data" />
                            <div
                                style={{
                                    marginTop: "5px",
                                    fontWeight: "500",
                                    fontSize: "25px",
                                    fontFamily: "Noto Sans, sans-serif",
                                }}
                            >
                                There are no Contact Persons to show.
                            </div>
                        </div>
                    ) : (
                        <TableContainer component={Paper}>
                            <Table sx={{ minWidth: 650 }} size="small" aria-label="contacts table">
                                <TableHead>
                                    <TableRow className={classes.tableHeadRow}>
                                        <TableCell className={classes.tableHeadCell}>Sr. No.</TableCell>
                                        <TableCell className={classes.tableHeadCell}>Name</TableCell>
                                        <TableCell className={classes.tableHeadCell}>Email</TableCell>
                                        <TableCell className={classes.tableHeadCell}>Phone</TableCell>
                                        <TableCell className={classes.tableHeadCell}>Status</TableCell>
                                        <TableCell className={classes.tableHeadCell}>Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody className={classes.noHorizontalBorder}>
                                    {contacts.map((val, index) => (
                                        <TableRow key={val.contact_id ?? index}>
                                            <TableCell className={classes.tableBodyCell}>
                                                {(page - 1) * 10 + index + 1}
                                            </TableCell>
                                            <TableCell className={classes.tableBodyCell}>
                                                <h4>{val.name}</h4>
                                            </TableCell>
                                            <TableCell className={classes.tableBodyCell}>
                                                <h4>{val.email}</h4>
                                            </TableCell>
                                            <TableCell className={classes.tableBodyCell}>
                                                <h4>{val.phone}</h4>
                                            </TableCell>
                                            <TableCell className={classes.tableBodyCell}>
                                                {val.is_deleted === "N" && val.is_active === "Y"
                                                    ? "Active"
                                                    : "Inactive"}
                                            </TableCell>
                                            <TableCell className={classes.tableBodyCell}>
                                                <ContactMoreMenu
                                                    contact={val}
                                                    onEditClick={openEditDialog}
                                                    onToggleStatus={handleToggleStatus}
                                                />
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </Grid>

                {/* Pagination same position as Customers */}
                <Stack
                    spacing={2}
                    sx={{
                        padding: "2%",
                        display: "flex",
                        flexDirection: "row",
                        justifyContent: "end",
                        width: "100%",
                    }}
                >
                    <Pagination
                        count={pageCount}
                        page={page}
                        onChange={handlePageChange}
                        showFirstButton
                        showLastButton
                    />
                </Stack>
            </Grid>
            <CustomModel
                open={editOpen}
                handleClose={closeEditDialog}
                handleDialog={closeEditDialog}
                title="Edit Contact Person"
                action
                actions={[
                    { label: "Cancel", onClick: closeEditDialog },
                    { label: "Save", onClick: handleEditSave },
                ]}
            >
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <TextField
                            label="Name"
                            fullWidth
                            size="small"
                            value={editForm.name}
                            onChange={(e) =>
                                setEditForm((prev) => ({ ...prev, name: e.target.value }))
                            }
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            label="Email"
                            fullWidth
                            size="small"
                            value={editForm.email}
                            onChange={(e) =>
                                setEditForm((prev) => ({ ...prev, email: e.target.value }))
                            }
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            label="Phone"
                            fullWidth
                            size="small"
                            value={editForm.phone}
                            onChange={(e) =>
                                setEditForm((prev) => ({ ...prev, phone: e.target.value }))
                            }
                        />
                    </Grid>
                </Grid>
            </CustomModel>
        </>
    );
};

export default CustomerContacts;
